// AppShell — the authenticated app chrome.
//
// Header architecture (Phase 35.2 polish — fixes wrapping/density issues
// surfaced in the v1 nav):
//
//   ─────────────────────────────────────────────────────────────────────
//   ░ logo + wordmark ░░░  primary 5 + More ▾   ░░░  Globe ▾ + Avatar ▾ ░
//   ─────────────────────────────────────────────────────────────────────
//
//   Primary nav (≥lg / 1024px): Dashboard · Portfolio · New Deal · Copilot · Reports
//   More dropdown:              Manage · Messages · Partnerships · Investor Portal
//   Tablet (md, 768–1023px):    Dashboard · Portfolio · New Deal · Copilot + More
//                               (Reports moves into More)
//   Mobile (<md):               hamburger → right-side Sheet drawer
//   User avatar dropdown:       Settings · Admin (if is_admin) · Log out
//   Language switcher:          compact = Globe icon + chevron only
//
// Every nav link carries `whitespace-nowrap` so multi-word labels never wrap.

import { useState } from "react";
import { Link, NavLink, useMatch, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ImpersonationBanner from "@/components/admin/ImpersonationBanner";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import MaintenanceOverlay from "@/components/MaintenanceOverlay";
import {
  Bot,
  Building2,
  ChevronDown,
  Columns3,
  FileBarChart,
  Handshake,
  LayoutDashboard,
  LineChart,
  LogOut,
  Menu,
  MessageSquare,
  Settings as SettingsIcon,
  ShieldCheck,
  Sparkles,
  Telescope,
  Wrench,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────
//  Nav definition — single source of truth. `tier` controls which
//  responsive bucket the item belongs in:
//
//    "primary-lg" = visible inline ≥lg (1024px)
//    "primary-md" = visible inline ≥md (768px); demoted into More <lg
//    "more"       = always in the More dropdown
//
//  Mobile drawer shows the union (everything).
// ─────────────────────────────────────────────────────────────────────
function useNavConfig() {
  const { t } = useTranslation();
  return [
    { to: "/dashboard",       label: t("nav.dashboard"),                                                Icon: LayoutDashboard, tier: "primary-md", testid: "nav-dashboard" },
    { to: "/portfolio",       label: t("nav.portfolio"),                                                Icon: Building2,       tier: "primary-md", testid: "nav-portfolio" },
    { to: "/deals/new",       label: t("nav.new_deal"),                                                 Icon: Sparkles,        tier: "primary-md", testid: "nav-new-deal", badge: "+" },
    { to: "/copilot",         label: t("nav.copilot"),                                                  Icon: Bot,             tier: "primary-md", testid: "nav-copilot" },
    { to: "/reports",         label: t("nav.reports"),                                                  Icon: FileBarChart,    tier: "primary-md", testid: "nav-reports" },
    { to: "/manage",          label: t("nav.manage"),                                                   Icon: Wrench,          tier: "primary-md", testid: "nav-manage" },
    { to: "/manage/messages", label: t("nav.messages"),                                                 Icon: MessageSquare,   tier: "primary-md", testid: "nav-messages" },
    { to: "/partnerships",    label: t("nav.partnerships",  { defaultValue: "Partnerships" }),          Icon: Handshake,       tier: "primary-md", testid: "nav-partnerships" },
    { to: "/investor",        label: t("nav.investor",      { defaultValue: "Investor Portal" }),       Icon: LineChart,       tier: "primary-md", testid: "nav-investor-portal" },
    { to: "/pipeline",        label: t("nav.pipeline",      { defaultValue: "Pipeline" }),              Icon: Columns3,        tier: "primary-md", testid: "nav-pipeline" },
    { to: "/discovery",       label: t("nav.discovery",     { defaultValue: "Discovery" }),             Icon: Telescope,       tier: "primary-md", testid: "nav-discovery" },
  ];
}

// Icon-only nav button used in both desktop primary rows. The text label is
// hidden visually but remains in `aria-label` (announced by screen readers)
// and the radix Tooltip (shown on hover/focus after ~300ms).
//
// Active state is a filled indigo pill + a 2 px indigo underline anchored to
// the icon's bottom edge — both cues fire together because, without text,
// one cue alone is too quiet at speed.
//
// Note: we resolve `isActive` via `useMatch` and assemble a plain-string
// className, rather than using NavLink's `className={fn}` render-prop. Radix
// `<TooltipTrigger asChild>` merges children props through @radix-ui/react-slot
// which stringifies any function passed via className, so the function-form
// never gets evaluated and the active styles silently drop out.
function NavItem({ item, className = "" }) {
  const { Icon, label } = item;
  const match = useMatch({ path: item.to, end: false });
  const isActive = !!match;
  const stateClass = isActive
    ? "bg-indigo-50 text-indigo-600"
    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <NavLink
          to={item.to}
          data-testid={item.testid}
          aria-label={label}
          className={`group relative inline-flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${stateClass} ${className}`}
        >
          <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
          {/* Tiny "+" affordance on New Deal — soft indigo dot, only when not active */}
          {item.badge && !isActive && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-indigo-600 text-[9px] font-bold leading-none text-white shadow-sm ring-2 ring-white"
            >
              {item.badge}
            </span>
          )}
          {/* Active underline cue — 2px indigo bar under the icon */}
          {isActive && (
            <span
              aria-hidden="true"
              className="absolute -bottom-px left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-full bg-indigo-600"
            />
          )}
        </NavLink>
      </TooltipTrigger>
      <TooltipContent side="bottom" sideOffset={6} className="text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const navItems = useNavConfig();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = !!user?.is_admin;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const initials = (user?.name || user?.email || "?")
    .split(/[\s@.]/)
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // All nav items render as inline icon buttons at ≥lg (1024 px). Below
  // lg the whole row collapses into the hamburger drawer — there is no
  // intermediate "More" overflow dropdown.

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <MaintenanceOverlay />
      <ImpersonationBanner />
      <AnnouncementBanner />

      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 pt-safe backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:px-6 lg:px-8">
          {/* ── Left: brand ──────────────────────────────────────────── */}
          <Link
            to="/dashboard"
            data-testid="app-logo"
            className="flex flex-shrink-0 items-center gap-2"
          >
            <img
              src="/logo.png"
              alt="runidea.ai"
              className="h-8 w-8 rounded-md object-cover"
            />
            <span className="whitespace-nowrap text-base font-semibold tracking-tight">
              runidea.ai
            </span>
          </Link>

          {/* ── Middle: primary nav (≥lg only) ───────────────────────── */}
          <TooltipProvider delayDuration={300} skipDelayDuration={150}>
            <nav
              className="hidden flex-1 items-center gap-1 lg:flex lg:ml-8"
              aria-label="Primary"
              data-testid="primary-nav"
            >
              {navItems.map((item) => (
                <NavItem key={item.to} item={item} />
              ))}
            </nav>
          </TooltipProvider>

          {/* ── Right: language + user / hamburger ───────────────────── */}
          <div className="ml-auto flex flex-shrink-0 items-center gap-1.5">
            {/* Compact language switcher (icon + chevron only) */}
            <div className="hidden sm:block">
              <LanguageSwitcher compact />
            </div>

            {/* User avatar dropdown — desktop & tablet */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-1.5 py-1 text-sm font-medium text-slate-700 transition-all hover:border-slate-300 hover:shadow-sm hover:ring-2 hover:ring-indigo-100"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
                    {initials}
                  </span>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-60"
                data-testid="user-menu"
              >
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col">
                    <span
                      data-testid="user-name"
                      className="truncate whitespace-nowrap text-sm font-medium text-slate-900"
                    >
                      {user?.name || "—"}
                    </span>
                    <span className="truncate text-xs text-slate-500">
                      {user?.email}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => navigate("/settings")}
                  data-testid="menu-settings"
                  className="cursor-pointer"
                >
                  <SettingsIcon className="mr-2 h-4 w-4" /> {t("nav.settings")}
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem
                    onSelect={() => navigate("/admin")}
                    data-testid="menu-admin"
                    className="cursor-pointer"
                  >
                    <ShieldCheck className="mr-2 h-4 w-4 text-indigo-600" />
                    RunIdea Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={handleLogout}
                  data-testid="menu-logout"
                  className="cursor-pointer text-red-600 focus:text-red-700"
                >
                  <LogOut className="mr-2 h-4 w-4" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile hamburger (<md) */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  data-testid="mobile-nav-trigger"
                  className="ml-1 flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-700 transition-colors hover:bg-slate-100 lg:hidden"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-72 sm:w-80"
                data-testid="mobile-nav-drawer"
              >
                <div className="mb-6 flex items-center gap-2">
                  <img
                    src="/logo.png"
                    alt="runidea.ai"
                    className="h-7 w-7 rounded-md object-cover"
                  />
                  <span className="text-sm font-semibold tracking-tight">
                    runidea.ai
                  </span>
                </div>
                <nav className="flex flex-col gap-1" aria-label="Mobile">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`m-${item.testid}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-indigo-50 text-indigo-700"
                            : "text-slate-700 hover:bg-slate-100"
                        }`
                      }
                    >
                      <item.Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
                      {item.label}
                    </NavLink>
                  ))}
                  <div className="my-2 h-px bg-slate-200" />
                  <NavLink
                    to="/settings"
                    onClick={() => setMobileOpen(false)}
                    data-testid="m-nav-settings"
                    className={({ isActive }) =>
                      `flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-indigo-50 text-indigo-700"
                          : "text-slate-700 hover:bg-slate-100"
                      }`
                    }
                  >
                    <SettingsIcon className="h-4 w-4" /> {t("nav.settings")}
                  </NavLink>
                  {isAdmin && (
                    <NavLink
                      to="/admin"
                      onClick={() => setMobileOpen(false)}
                      data-testid="m-nav-admin"
                      className={({ isActive }) =>
                        `flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-indigo-50 text-indigo-700"
                            : "text-slate-700 hover:bg-slate-100"
                        }`
                      }
                    >
                      <ShieldCheck className="h-4 w-4 text-indigo-600" /> RunIdea Admin
                    </NavLink>
                  )}
                  <button
                    type="button"
                    onClick={async () => {
                      setMobileOpen(false);
                      await handleLogout();
                    }}
                    data-testid="m-menu-logout"
                    className="mt-1 flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-left text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                  >
                    <LogOut className="h-4 w-4" /> {t("nav.logout")}
                  </button>
                  <div className="mt-3 border-t border-slate-200 pt-3">
                    <LanguageSwitcher compact={false} align="start" />
                  </div>
                  {user && (
                    <div className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-xs">
                      <div className="font-medium text-slate-900">
                        {user.name}
                      </div>
                      <div className="truncate text-slate-500">
                        {user.email}
                      </div>
                    </div>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {children}
      </main>

      <div className="border-t border-slate-200 bg-slate-50 py-4">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p
            className="text-xs uppercase tracking-[0.18em] text-slate-400"
            data-testid="chrome-tagline"
          >
            {t("brand.tagline")}
          </p>
        </div>
      </div>
    </div>
  );
}

export { Button };
